import React, { useEffect } from 'react';
import SEO from '../components/SEO';
import StructuredData, { generateFAQSchema } from '../components/StructuredData';
import './ConnectPage.css';

const ConnectPage = () => {
    useEffect(() => {
        // Animate elements on mount
        const animateElements = () => {
            const elements = document.querySelectorAll('.animate-line');
            elements.forEach((element, index) => {
                setTimeout(() => {
                    element.classList.add('visible');
                }, 300 + (index * 200));
            });
        };

        animateElements();
    }, []);

    const faqs = [
        {
            question: "What services does Jacob Faulkner offer?",
            answer: "Jacob Faulkner offers 3D production, interactive installations, creative technology, real-time experiences, and bespoke tool building. Services include Cinema 4D, Blender, Touch Designer, Unity, and custom software development."
        },
        {
            question: "How can I contact Jacob Faulkner?",
            answer: "You can contact Jacob Faulkner via email at jacob@ordos.co.uk or through Instagram @ordos.studio for collaboration inquiries and project discussions."
        },
        {
            question: "What types of projects does Jacob Faulkner work on?",
            answer: "Jacob Faulkner works on a wide range of projects including 3D visuals for commercials and music videos, immersive installations, interactive art, live visuals, VJ sets, VR experiences, and custom automation tools."
        }
    ];

    return (
        <>
            <SEO
                title="Contact & Collaboration | Jacob Faulkner"
                description="Partner with Jacob Faulkner for 3D visuals, immersive experiences, and creative technology projects. Specializing in interactive installations, real-time graphics, and bespoke tool building."
                keywords="contact, collaboration, freelance 3D artist, creative technologist, hire, commission, Jacob Faulkner, Ordos, Bristol"
                url="/connect"
            />
            <StructuredData data={generateFAQSchema(faqs)} />
            <div className="connect-page">
            <div className="connect-content">
                {/* Main Connect Section */}
                <section className="connect-section">
                    <h1 className="animate-line">Let's collaborate</h1>
                    
                    <div className="connect-text">
                        <p className="animate-line">
                            I partner with individuals, brands, and agencies to craft 3D visuals, immersive experiences, and creative technology projects. If you're looking for something more specific, or would like to see my full offline portfolio, just drop me a message — I'd love to hear from you.
                        </p>
                    </div>
                </section>

                {/* Social Media Links Section */}
                <section className="social-links-section">
                    <div className="social-links">
                        <a 
                            href="https://www.linkedin.com/in/jacob-ordos/" 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="social-link animate-line"
                            aria-label="LinkedIn"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="currentColor"/>
                            </svg>
                        </a>
                        <a 
                            href="https://instagram.com/ordos.studio" 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="social-link animate-line"
                            aria-label="Instagram"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" fill="currentColor"/>
                            </svg>
                        </a>
                    </div>
                </section>

                {/* Contact Links Section */}
                <section className="contact-links-section">
                    <h2 className="animate-line">Get in touch</h2>
                    <div className="contact-links">
                        <div className="contact-item animate-line">
                            <a href="mailto:jacob@ordos.co.uk" className="contact-link">
                                <span>jacob@ordos.co.uk</span>
                            </a>
                        </div>
                        <div className="contact-item animate-line">
                            <a href="https://www.instagram.com/ordos.studio" target="_blank" rel="noopener noreferrer" className="contact-link">
                                <span>@ordos.studio</span>
                            </a>
                        </div>
                    </div>
                </section>

                {/* Services Section */}
                <section className="services-section">
                    <h2 className="animate-line">What I offer</h2>
                    <div className="services-grid">
                        <div className="service-item animate-line">
                            <h3>3D Production</h3>
                            <p>Cinema 4D, Blender, and real-time 3D graphics for commercials, music videos, and digital experiences.</p>
                        </div>
                        <div className="service-item animate-line">
                            <h3>Interactive Installations</h3>
                            <p>Touch Designer, Unity, and custom software for immersive installations and interactive art.</p>
                        </div>
                        <div className="service-item animate-line">
                            <h3>Creative Technology</h3>
                            <p>End-to-end project support from concept development through to final execution.</p>
                        </div>
                        <div className="service-item animate-line">
                            <h3>Real-time Experiences</h3>
                            <p>Live visuals, VJ sets, and real-time graphics for events and performances.</p>
                        </div>
                        <div className="service-item animate-line">
                            <h3>Bespoke Tool Building</h3>
                            <p>Custom automation tools and scripts to streamline workflows and automate repetitive tasks.</p>
                        </div>
                    </div>
                </section>

                {/* Footer */}
                <footer className="connect-footer">
                    <p className="animate-line">© <span id="year"></span> Jacob Faulkner All rights reserved.</p>
                </footer>
            </div>
        </div>
        </>
    );
};

export default ConnectPage; 